import { Component, ChangeDetectionStrategy, inject, signal, computed, effect, OnInit, untracked } from '@angular/core';
import { RouterLink, RouterLinkActive, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '@core/services/auth.service';
import { ProjectContextService } from '@core/services/project-context.service';
import { UserNotificationService } from '@data-access/services/user-notification.service';
import { VersionService } from '@data-access/services/version.service';
import { WELLS_NPT_ENABLED, WellsContextService } from '@core/services/wells-context.service';
import { WellsSupplierContextService } from '@core/services/wells-supplier-context.service';

export interface SidebarItem {
  label: string;
  route: string | null;
  icon: string;
  /** Match the route exactly when highlighting (used for Home / Projects). */
  exact: boolean;
  queryParams?: Record<string, string>;
  /** Only show this item to admins. */
  adminOnly?: boolean;
}

export interface QuickCreateItem {
  label: string;
  route: string;
  icon: string;
}

const ROLE_LABELS: Record<string, string> = {
  'admin': 'Administrator',
  'quality-engineer': 'Quality Advisor / Engineer',
  'inspector': 'Inspector',
  'manager': 'Manager',
  'supplier': 'Supplier',
  'external-inspection-coordinator': 'External Inspection Coordinator',
  'external-supplier-coordinator': 'External Supplier Coordinator',
};

/**
 * The External Inspector's whole job in QMS is inspection work, so their shell is trimmed to it.
 *
 * They get a single "Inspection Management" entry pointing at the enhanced inspection module (the
 * classic `/inspections-hub` duplicate and the "Enhanced" copy under More are both dropped, so there
 * is exactly one way in), plus the simplified NC/CAPA and read-only surfaces they genuinely use.
 * Everything they cannot act on — Administration, enhanced/classic quality modules and the create
 * shortcuts for records they are not allowed to raise — is left out rather than shown and then rejected.
 */
const EXTERNAL_INSPECTOR_NAV: SidebarItem[] = [
  { label: 'Home', route: '/home', icon: 'home', exact: true },
  { label: 'Inspection Management', route: '/inspection-enhanced', icon: 'fact_check', exact: false },
  { label: 'Non-Conformance Management', route: '/ncrs', icon: 'report_problem', exact: false },
  { label: 'CAPA Management', route: '/capas', icon: 'track_changes', exact: false },
  { label: 'Dashboards', route: '/dashboard', icon: 'dashboard', exact: false, queryParams: { tab: 'overview' } },
];

/**
 * No quick-create shortcuts for the External Inspector.
 *
 * A report only exists against an assignment (it inherits its contract, supplier and cost lines from
 * one), so there is no meaningful "blank report" destination — reports are raised with the
 * "Create Report" button on the assignment itself. An empty list hides the Quick Create section.
 */
const EXTERNAL_INSPECTOR_QUICK_CREATE: QuickCreateItem[] = [];

@Component({
  selector: 'qms-shell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, RouterLinkActive, MatIconModule, MatButtonModule, MatMenuModule, MatTooltipModule, MatBadgeModule],
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.scss',
})
export class ShellComponent implements OnInit {
  private auth = inject(AuthService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private notificationService = inject(UserNotificationService);
  private versionService = inject(VersionService);
  private projectContext = inject(ProjectContextService);
  readonly wellsContext = inject(WellsContextService, { optional: true });
  private readonly supplierContext = inject(WELLS_NPT_ENABLED) ? inject(WellsSupplierContextService) : null;
  readonly supplierActive = computed(() => this.supplierContext?.restricted() ?? false);
  readonly wellsActive = computed(() => this.supplierActive() || (this.wellsContext?.active() ?? false));
  private readonly notificationContext = effect(() => {
    const active = this.wellsActive();
    untracked(() => {
      if (active) {
        this.notifOpen.set(false);
        this.notificationService.stopPolling?.();
      } else {
        this.notificationService.startPolling();
      }
    });
  });

  readonly currentUser = this.auth.currentUser;
  readonly hasMultipleRoles = this.auth.hasMultipleRoles;
  readonly isAdmin = this.auth.isAdmin;
  readonly canSwitchWellsContext = computed(() => this.auth.isAdmin() && this.auth.userRole() === 'admin');

  /** Initials shown in the avatar circle, derived from the user's display name. */
  readonly userInitials = computed(() => {
    const name = this.currentUser()?.displayName?.trim();
    if (!name) return '?';
    const parts = name.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase() || '?';
  });

  /** Friendly label for the user's active role. */
  readonly roleLabel = computed(() => {
    const role = this.auth.userRole();
    return role ? ROLE_LABELS[role] ?? role : '';
  });

  /** Label shown in the navbar project badge. */
  readonly projectBadgeLabel = computed(() => {
    const project = this.projectContext.currentProject();
    if (project) {
      return project.name;
    }
    const tag = this.projectContext.currentTagFilter();
    if (tag) {
      return tag;
    }
    const location = this.projectContext.locationFilter();
    if (location) {
      return `All ${location} projects`;
    }
    if (this.isAdmin()) {
      return 'All projects';
    }
    return 'Select a project';
  });

  /** Whether a specific project is currently selected (vs. an all-projects view). */
  readonly hasProjectSelected = computed(() =>
    this.projectContext.currentProject() !== null || this.projectContext.currentTagFilter() !== null);
  readonly sidebarCollapsed = signal(false);
  readonly mobileNavOpen = signal(false);
  readonly notifOpen = signal(false);
  readonly searchQuery = signal('');
  readonly currentYear = new Date().getFullYear();
  readonly appVersion = this.versionService.version;
  readonly notifications = this.notificationService.notifications;
  readonly notifCount = this.notificationService.count;
  readonly notifLoading = this.notificationService.loading;

  /** Primary sidebar navigation — mirrors the QMS landing wireframe. */
  private readonly defaultPrimaryNav: SidebarItem[] = [
    { label: 'Home', route: '/home', icon: 'home', exact: true },
    { label: 'Projects', route: '/projects', icon: 'folder', exact: true },
    // NC and CAPA entries open the canonical simplified modules.
    { label: 'Non-Conformance Management', route: '/ncrs', icon: 'report_problem', exact: false },
    { label: 'Inspection Management', route: '/inspection-enhanced', icon: 'fact_check', exact: false },
    { label: 'CAPA Management', route: '/capas', icon: 'track_changes', exact: false },
    { label: 'Audits', route: '/audits', icon: 'verified_user', exact: false },
    { label: 'Observations', route: '/observations', icon: 'visibility', exact: false },
    { label: 'Dashboards', route: '/dashboard', icon: 'dashboard', exact: false, queryParams: { tab: 'overview' } },
    { label: 'Supply Chain Quality', route: '/supply-chain', icon: 'inventory_2', exact: false },
  ];

  /** Additional modules kept reachable so no existing functionality is lost. */
  private readonly defaultSecondaryNav: SidebarItem[] = [];

  /** Quick-create actions wired to existing module create routes. */
  private readonly defaultQuickCreate: QuickCreateItem[] = [
    { label: 'New Audit', route: '/audits/new', icon: 'add' },
    { label: 'New NCR', route: '/ncrs/new', icon: 'add' },
    { label: 'New Inspection', route: '/inspection-enhanced/assignments/new', icon: 'add' },
    { label: 'New CAPA', route: '/capas/new', icon: 'add' },
    { label: 'New Observation', route: '/observations/new', icon: 'add' },
  ];

  /**
   * True when the user is *acting as* an External Inspector. This reads the active role rather than
   * the union of roles held, so an admin who switches into the inspector role sees exactly what a
   * real External Inspector sees — which is the whole point of role switching during UAT.
   */
  readonly isExternalInspector = computed(() => this.auth.userRole() === 'inspector');

  readonly primaryNav = computed<SidebarItem[]>(() =>
    this.supplierActive() ? [{ label: 'Assigned Wells incidents', route: '/wells-supplier', icon: 'assignment', exact: false }] :
      this.wellsActive() ? [
        { label: 'Dashboard', route: '/wells-npt/dashboard', icon: 'dashboard', exact: true },
        { label: 'Incidents', route: '/wells-npt', icon: 'report_problem', exact: true },
      ] :
      this.isExternalInspector() ? EXTERNAL_INSPECTOR_NAV : this.defaultPrimaryNav);

  readonly secondaryNav = computed<SidebarItem[]>(() =>
    this.wellsActive() || this.isExternalInspector() ? [] : this.defaultSecondaryNav);

  readonly quickCreate = computed<QuickCreateItem[]>(() =>
    this.wellsActive() || this.isExternalInspector() ? EXTERNAL_INSPECTOR_QUICK_CREATE : this.defaultQuickCreate);

  ngOnInit(): void {
    // Load real notifications from API
    if (this.supplierContext) this.supplierContext.refresh().subscribe(allowed => { if (!allowed && !this.supplierActive()) this.wellsContext?.refresh().subscribe(); });
    else this.wellsContext?.refresh().subscribe();
    // Load app version from API
    this.versionService.loadVersion().subscribe();
  }

  toggleSidebar(): void {
    this.sidebarCollapsed.update(v => !v);
  }

  toggleMobileNav(): void {
    this.mobileNavOpen.update(v => !v);
  }

  closeMobileNav(): void {
    this.mobileNavOpen.set(false);
  }

  toggleNotifications(): void {
    this.notifOpen.update(v => !v);
  }

  closeNotifications(): void {
    this.notifOpen.set(false);
  }

  openGlossary(): void {
    import('@shared/components/glossary-dialog/glossary-dialog.component').then(m => {
      this.dialog.open(m.GlossaryDialogComponent, {
        width: '95vw',
        maxWidth: '95vw',
        height: '90vh',
      });
    });
  }

  /**
   * Global search submit handler. Navigates to the global search results page with the current
   * query. Empty queries are ignored so pressing Enter on a blank box is a harmless no-op.
   */
  onSearchSubmit(): void {
    if (this.wellsActive()) return;
    const q = this.searchQuery().trim();
    if (!q) return;
    this.closeMobileNav();
    this.router.navigate(['/search'], { queryParams: { q } });
  }

  logout(): void {
    this.auth.logout();
    this.router.navigate(['/login']);
  }

}
